import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { IApiType } from '../../types'

const QUERY_NAME = 'listLocationMetricValues'
const query = gql`
  query (
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
  ) {
    ${QUERY_NAME}(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        locationId
        metricData
      }
    }
  }
`

export const locationSalesByOriginConfigs = {
  name: 'string',
  sales: 'price',
  checkCount: 'number',
  checkAverage: 'price',
  percentSales: 'percent',
} as const

const useLocationSalesByOrigin = (): IApiType => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter } = useGroupFilter()

  const iFilter = {
    location_ids: groupFilter?.ids,
    metric_groups: [
      'Expo Module - Revenue Center Sales Sales By Origin',
      'Expo Module - Revenue Center Check Count Sales By Origin',
      'Expo Module - Revenue Center Check Average Sales By Origin',
      'Expo Module - Fundamental Sales',
    ],
  }

  const { data, loading } = useQuery(query, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter,
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo(() => {
      const nodes = data?.[QUERY_NAME].nodes as
        | {
            locationId: string
            metricData: {
              [key: string]: {
                name: string
                unit: string
                value: number
              }
            }
          }[]
        | undefined

      if (!nodes) return null
      const mappedData: {
        [origin: string]: {
          [kpi: string]: number | string
        }
      } = {}
      const metricData = nodes[0]?.metricData

      let netSales = 0
      for (const key in metricData) {
        if (key === 'net_sales') {
          if (metricData[key].unit === 'CENT') {
            netSales = metricData[key].value / 100
          } else {
            netSales = metricData[key].value
          }
          continue
        }

        const { kpi, origin } = (() => {
          const kpiAndOriginSales = key.split('revenue_center_sales_')?.[1]
          if (kpiAndOriginSales)
            return {
              kpi: 'sales',
              origin: kpiAndOriginSales,
            }

          const kpiAndOriginCheckCount = key.split(
            'revenue_center_check_count_',
          )?.[1]
          if (kpiAndOriginCheckCount)
            return {
              kpi: 'checkCount',
              origin: kpiAndOriginCheckCount,
            }

          const kpiAndOriginCheckAvg = key.split(
            'revenue_center_check_average_',
          )?.[1]
          if (kpiAndOriginCheckAvg)
            return {
              kpi: 'checkAverage',
              origin: kpiAndOriginCheckAvg,
            }

          return {
            kpi: '',
            origin: '',
          }
        })()

        if (!mappedData[origin]) {
          mappedData[origin] = {
            name: metricData[key].name
              .split(' - ')[1]
              .replace(/\s* Sales By Origin$/, ''),
            [kpi]: metricData[key].value,
          }
        } else {
          mappedData[origin][kpi] = metricData[key].value
        }
      }

      const rows = Object.values(mappedData)
        ?.filter((n) => n.sales)
        ?.map((n) => {
          return {
            ...n,
            percentSales: (100 * (n.sales as number)) / netSales,
            id: n.name as string,
            parentId: 'root',
          }
        })

      return rows
    }, [groupFilter, data]),
    loading,
  }
}

export default useLocationSalesByOrigin
